import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, CheckCircle2, ArrowRight, ArrowLeft,
  Mail, FileText, Calendar, MessageSquare, Gift, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const phases = [
  {
    title: 'Phase 1: Before They Sign',
    icon: FileText,
    timeframe: 'During Sales Process',
    description: 'Set expectations early to avoid surprises later.',
    tasks: [
      { task: 'Provide a clear scope document', detail: 'Define exactly what\'s included (and what\'s not). Prevent scope creep by being specific upfront.' },
      { task: 'Explain your process', detail: 'Walk through how you work: timelines, communication methods, what you need from them.' },
      { task: 'Discuss communication preferences', detail: 'How often will you update them? Email, phone, Slack? Set expectations now.' },
      { task: 'Introduce key team members', detail: 'If others will work on their project, introduce them before signing.' },
      { task: 'Be honest about timelines', detail: 'Under-promise and over-deliver. Never promise what you can\'t deliver.' },
    ]
  },
  {
    title: 'Phase 2: Welcome & Kickoff',
    icon: Gift,
    timeframe: 'Days 1-3',
    description: 'Make new clients feel valued and informed from the start.',
    tasks: [
      { task: 'Send a welcome email immediately', detail: 'Thank them, confirm next steps, and provide key contact information.' },
      { task: 'Provide a welcome packet', detail: 'Include: your contact info, office hours, how to reach you urgently, FAQs, and what to expect.' },
      { task: 'Send the intake questionnaire', detail: 'Gather all the information you need to start. Make it comprehensive but not overwhelming.' },
      { task: 'Schedule the kickoff call', detail: 'Get it on the calendar within the first week while enthusiasm is high.' },
      { task: 'Grant access to project tools', detail: 'If using project management software, invite them and provide a quick tutorial.' },
      { task: 'Send a small welcome gift (optional)', detail: 'A thoughtful touch that makes you memorable. Doesn\'t need to be expensive.' },
    ]
  },
  {
    title: 'Phase 3: Discovery & Planning',
    icon: Calendar,
    timeframe: 'Week 1-2',
    description: 'Gather information and create a clear plan together.',
    tasks: [
      { task: 'Conduct a thorough kickoff meeting', detail: 'Review goals, discuss their business, understand their audience, and align on success metrics.' },
      { task: 'Review all provided materials', detail: 'Existing brand assets, competitors, inspirations, past work - study everything they share.' },
      { task: 'Document requirements clearly', detail: 'Write up everything discussed and get their confirmation. Avoid "I thought you meant..."' },
      { task: 'Create a project timeline', detail: 'Break the project into phases with clear milestones and deadlines.' },
      { task: 'Identify potential blockers', detail: 'What could slow things down? Address dependencies early.' },
      { task: 'Establish approval process', detail: 'Who approves what? How many revision rounds? Get this in writing.' },
    ]
  },
  {
    title: 'Phase 4: Active Project Phase',
    icon: Clock,
    timeframe: 'Ongoing',
    description: 'Keep clients informed and involved without overwhelming them.',
    tasks: [
      { task: 'Send weekly progress updates', detail: 'Even if there\'s not much to report. Silence breeds anxiety. A quick email takes 5 minutes.' },
      { task: 'Share work in progress early', detail: 'Don\'t wait until it\'s perfect. Early feedback prevents late-stage disasters.' },
      { task: 'Be proactive about delays', detail: 'If something will be late, tell them immediately with a new timeline. Never surprise them.' },
      { task: 'Document all decisions', detail: 'Follow up calls and meetings with written summaries. Create a paper trail.' },
      { task: 'Make it easy to give feedback', detail: 'Use tools like Loom for video feedback, or markup tools for design reviews.' },
      { task: 'Celebrate milestones', detail: 'When you hit a major milestone, acknowledge it. Builds excitement and trust.' },
    ]
  },
  {
    title: 'Phase 5: Delivery & Handoff',
    icon: Mail,
    timeframe: 'Project End',
    description: 'End strong with a clear handoff and lasting positive impression.',
    tasks: [
      { task: 'Conduct final walkthrough', detail: 'Review everything together. Train them on anything they need to manage.' },
      { task: 'Provide documentation', detail: 'User guides, login credentials, maintenance instructions - all in one organized document.' },
      { task: 'Transfer all assets', detail: 'Deliver source files, passwords, and access credentials. Nothing should be held hostage.' },
      { task: 'Review the project together', detail: 'What went well? What could improve? Gather feedback while it\'s fresh.' },
      { task: 'Request a testimonial', detail: 'If they\'re happy, ask for a review or case study while enthusiasm is high.' },
      { task: 'Outline ongoing support', detail: 'Explain how to reach you for future needs. Maintenance plans, support hours, etc.' },
    ]
  },
  {
    title: 'Phase 6: Post-Project Relationship',
    icon: MessageSquare,
    timeframe: 'Ongoing',
    description: 'The project ends, but the relationship shouldn\'t.',
    tasks: [
      { task: 'Check in after 30 days', detail: 'A quick email asking how things are going. Shows you care beyond the invoice.' },
      { task: 'Send helpful resources', detail: 'When you see articles or tools relevant to their business, share them.' },
      { task: 'Remember important dates', detail: 'Their business anniversary, launch date, or even birthdays. A quick note goes far.' },
      { task: 'Ask for referrals', detail: 'Happy clients are your best source of new business. Make it easy to refer you.' },
      { task: 'Offer exclusive benefits', detail: 'Early access to new services, loyalty discounts, or priority scheduling for past clients.' },
      { task: 'Stay in touch regularly', detail: 'Newsletter, occasional check-ins, holiday cards - maintain the relationship.' },
    ]
  },
];

const GuideOnboardingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-onboarding-page">
      <SEO 
        title="Client Onboarding Playbook - Create Amazing Client Experiences"
        description="Create a seamless onboarding experience that sets the tone for successful client relationships. From welcome emails to project handoff."
        keywords="client onboarding, customer onboarding, client experience, client welcome process, project kickoff, client management, freelancer client process"
        url="/resources/guides/client-onboarding"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full mb-6">
            <Users className="h-4 w-4 text-rose-600" />
            <span className="text-sm font-medium text-rose-700">Business Operations</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Client Onboarding Playbook
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            Create a seamless onboarding experience that sets the tone for successful client relationships. First impressions matter.
          </p>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-carbon-black mb-6">Why Onboarding Matters</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-rose-50 rounded-xl">
              <div className="text-3xl font-bold text-rose-600 mb-2">86%</div>
              <p className="text-sm text-slate-gray">of customers will pay more for a better experience</p>
            </div>
            <div className="text-center p-6 bg-rose-50 rounded-xl">
              <div className="text-3xl font-bold text-rose-600 mb-2">5x</div>
              <p className="text-sm text-slate-gray">cheaper to retain clients than acquire new ones</p>
            </div>
            <div className="text-center p-6 bg-rose-50 rounded-xl">
              <div className="text-3xl font-bold text-rose-600 mb-2">65%</div>
              <p className="text-sm text-slate-gray">of business comes from existing customers</p>
            </div>
          </div>
          <p className="text-slate-gray mt-8 leading-relaxed">
            A great onboarding experience does more than make clients feel good - it prevents problems down the road. Miscommunication, scope creep, and dissatisfaction often trace back to a poor start. Invest in onboarding, and everything else gets easier.
          </p>
        </div>
      </section>

      {/* Phases */}
      {phases.map((phase, phaseIdx) => {
        const IconComponent = phase.icon;
        return (
          <section key={phaseIdx} className={`py-12 sm:py-16 ${phaseIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-rose-600 mb-1">{phase.timeframe}</div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{phase.title}</h2>
                  <p className="text-slate-gray mt-1">{phase.description}</p>
                </div>
              </div>
              
              <div className="space-y-4 ml-16">
                {phase.tasks.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-carbon-black mb-1">{item.task}</h3>
                        <p className="text-sm text-slate-gray">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Templates Teaser */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-rose-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">Templates to Get Started</h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Don't start from scratch. We're building a library of templates including:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
            <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100">
              <CheckCircle2 className="h-5 w-5 text-rose-500" />
              <span className="text-carbon-black">Welcome Email Templates</span>
            </div>
            <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100">
              <CheckCircle2 className="h-5 w-5 text-rose-500" />
              <span className="text-carbon-black">Client Intake Questionnaire</span>
            </div>
            <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100">
              <CheckCircle2 className="h-5 w-5 text-rose-500" />
              <span className="text-carbon-black">Project Kickoff Agenda</span>
            </div>
            <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100">
              <CheckCircle2 className="h-5 w-5 text-rose-500" />
              <span className="text-carbon-black">Weekly Update Template</span>
            </div>
          </div>
          <p className="text-sm text-slate-gray mt-6">Coming soon to our Tools & Templates section</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need Help Systemizing Your Client Process?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            We help service businesses create smooth, scalable client experiences. Let's talk about your process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-rose-400 text-rose-600 hover:bg-rose-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideOnboardingPage;
